/**
 * Data for the Shopify store connection card, in org or platform scope.
 *
 * Budget: ONE query (whichever scope is enabled). It polls every 3s only
 * while an install is `exchanging` and inside the 60s claim lease (past it the
 * exchange has stalled and polling would never end); otherwise it refreshes
 * on window focus.
 * Every mutation writes its returned state into this query's cache instead of
 * refetching. The org context is reloaded only after a save that rewrote the
 * org row (domain, currency, credentials), and only for the caller's own org.
 *
 * Mutations never auto-retry: a save runs a Shopify exchange or a register,
 * and a retry after a proxy timeout would run it twice.
 */
import { useCallback, useEffect, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import type {
  ConnectionMode,
  ConnectionView,
  CustomAppView,
  OrgConnection,
  PlatformConnection,
} from './presentation';

export type SectionMode = 'org' | 'platform';

export const EXCHANGING_POLL_MS = 3_000;
/** The server's claim lease (`claim_shopify_custom_app_install`): an exchange older than this has stalled. */
export const EXCHANGING_STALE_MS = 60_000;

type ExchangeState = { status: string; statusAt?: string | null };
type PollableQuery = { state: { data?: { customApp: ExchangeState | null } | undefined } };

/** Still `exchanging` after the lease: the exchange died and nothing will move the row until the store retries. */
export function isExchangingStale(customApp: ExchangeState | null | undefined, nowMs: number = Date.now()): boolean {
  if (customApp?.status !== 'exchanging' || !customApp.statusAt) return false;
  const startedAt = Date.parse(customApp.statusAt);
  return Number.isFinite(startedAt) && nowMs - startedAt > EXCHANGING_STALE_MS;
}

export function pollWhileExchanging(query: PollableQuery): number | false {
  const customApp = query.state.data?.customApp;
  return customApp?.status === 'exchanging' && !isExchangingStale(customApp) ? EXCHANGING_POLL_MS : false;
}

/**
 * {@link isExchangingStale}, re-rendered at the moment the lease runs out
 * (an unchanged poll result does not re-render, so a timer does).
 */
export function useExchangingStale(customApp: ExchangeState | null | undefined): boolean {
  const [, setTick] = useState(0);
  const status = customApp?.status;
  const statusAt = customApp?.statusAt ?? null;
  useEffect(() => {
    if (status !== 'exchanging' || !statusAt) return undefined;
    const startedAt = Date.parse(statusAt);
    if (!Number.isFinite(startedAt)) return undefined;
    const wait = startedAt + EXCHANGING_STALE_MS - Date.now() + 1;
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setTick((tick) => tick + 1), wait);
    return () => clearTimeout(timer);
  }, [status, statusAt]);
  return isExchangingStale(customApp);
}

export interface SaveCredentialsInput {
  shopDomain: string;
  clientId?: string;
  clientSecret?: string;
  accessToken?: string;
}

/** A save outcome, normalised across both scopes. */
export type SaveResult =
  | { kind: 'connected'; mode: Exclude<ConnectionMode, 'none'> }
  | { kind: 'saved' }
  | { kind: 'install_required' }
  | { kind: 'needs_blue_monk' }
  | { kind: 'locked' }
  | { kind: 'error'; reason: string };

export function useShopifyConnection(organizationId: string, mode: SectionMode) {
  const utils = trpc.useUtils();
  const { organization, reloadOrganization } = useOrganization();
  const isCurrentOrg = organization?.id === organizationId;

  const orgQuery = trpc.shopifyIntegration.getConnectionStatus.useQuery(undefined, {
    enabled: mode === 'org',
    refetchInterval: pollWhileExchanging,
    refetchOnWindowFocus: true,
  });
  const platformQuery = trpc.platform.getShopifyConnection.useQuery(
    { organizationId },
    { enabled: mode === 'platform', refetchInterval: pollWhileExchanging, refetchOnWindowFocus: true },
  );
  const query = mode === 'platform' ? platformQuery : orgQuery;

  const saveOrg = trpc.shopifyIntegration.saveConnection.useMutation({ retry: false });
  const savePlatform = trpc.platform.saveShopifyConnection.useMutation({ retry: false });
  const setInstallLinkMutation = trpc.platform.setShopifyInstallLink.useMutation({ retry: false });
  const startSyncMutation = trpc.platform.startShopifySync.useMutation({ retry: false });

  const patchView = useCallback(
    (patch: (previous: ConnectionView) => ConnectionView) => {
      if (mode === 'platform') {
        utils.platform.getShopifyConnection.setData({ organizationId }, (previous) =>
          previous ? (patch(previous as ConnectionView) as PlatformConnection) : previous,
        );
      } else {
        utils.shopifyIntegration.getConnectionStatus.setData(undefined, (previous) =>
          previous ? (patch(previous as ConnectionView) as OrgConnection) : previous,
        );
      }
    },
    [mode, organizationId, utils],
  );

  const patchCustomApp = useCallback(
    (customApp: CustomAppView) => patchView((previous) => ({ ...previous, customApp })),
    [patchView],
  );

  const refreshOrganizationContext = useCallback(() => {
    if (!isCurrentOrg) return;
    void utils.organization.getOrganization.invalidate();
    reloadOrganization();
  }, [isCurrentOrg, reloadOrganization, utils]);

  const refetch = query.refetch;

  /**
   * Platform scope sends a Client ID + Secret to the platform admin save (which
   * registers a custom app on `shop_not_permitted` or `app_not_installed`).
   * Everything else (a token, a domain-only change) goes through the
   * org-scope save, which is only offered for the caller's own org.
   */
  const save = useCallback(
    async (input: SaveCredentialsInput): Promise<SaveResult> => {
      const hasPair = Boolean(input.clientId && input.clientSecret);
      if (mode === 'platform' && hasPair) {
        const result = await savePlatform.mutateAsync({
          organizationId,
          shopDomain: input.shopDomain,
          clientId: input.clientId ?? '',
          clientSecret: input.clientSecret ?? '',
        });
        if (result.outcome === 'error') return { kind: 'error', reason: result.reason };
        utils.platform.getShopifyConnection.setData({ organizationId }, result.connection);
        if (result.outcome === 'install_required') return { kind: 'install_required' };
        if (result.connection.mode === 'client_credentials') refreshOrganizationContext();
        return { kind: 'connected', mode: result.connection.mode === 'none' ? 'custom_app' : result.connection.mode };
      }

      const result = await saveOrg.mutateAsync({
        shopDomain: input.shopDomain,
        ...(hasPair ? { clientId: input.clientId, clientSecret: input.clientSecret } : {}),
        ...(!hasPair && input.accessToken ? { accessToken: input.accessToken } : {}),
      });
      switch (result.outcome) {
        case 'connected':
          patchView((previous) => ({ ...previous, mode: result.mode, shopDomain: result.shopDomain }));
          refreshOrganizationContext();
          return { kind: 'connected', mode: result.mode };
        case 'saved':
          patchView((previous) => ({ ...previous, shopDomain: result.shopDomain }));
          refreshOrganizationContext();
          return { kind: 'saved' };
        case 'locked':
          // A custom app was registered since this card loaded: show it.
          void refetch();
          return { kind: 'locked' };
        case 'needs_blue_monk':
          return { kind: 'needs_blue_monk' };
        default:
          return { kind: 'error', reason: result.reason };
      }
    },
    [mode, organizationId, patchView, refetch, refreshOrganizationContext, saveOrg, savePlatform, utils],
  );

  const saveInstallLink = useCallback(
    async (installLink: string) => {
      const result = await setInstallLinkMutation.mutateAsync({ organizationId, installLink });
      if (result.outcome === 'saved') patchCustomApp(result.customApp);
      return result;
    },
    [organizationId, patchCustomApp, setInstallLinkMutation],
  );

  const startSync = useCallback(async () => {
    const result = await startSyncMutation.mutateAsync({ organizationId });
    if (result.outcome === 'started') patchCustomApp(result.customApp);
    else if (result.outcome === 'already_syncing') {
      patchView((previous) =>
        previous.customApp ? { ...previous, customApp: { ...previous.customApp, syncEnabled: true } } : previous,
      );
    } else void refetch();
    return result;
  }, [organizationId, patchCustomApp, patchView, refetch, startSyncMutation]);

  return {
    data: query.data as ConnectionView | undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch,
    /** Token and domain-only saves exist only in org scope, i.e. for the caller's own org. */
    canUseOrgScope: mode === 'org' || isCurrentOrg,
    save,
    isSaving: saveOrg.isPending || savePlatform.isPending,
    saveInstallLink,
    isSavingInstallLink: setInstallLinkMutation.isPending,
    startSync,
    isStartingSync: startSyncMutation.isPending,
  };
}
